"use client"

import { useEffect, useState } from "react"
import { Box, Typography, IconButton, useTheme, useMediaQuery } from "@mui/material"
import { ProSidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar"
import {
  HomeOutlined as HomeIcon,
  Inventory2Outlined as InventoryIcon,
  MoneyOffOutlined as MoneyOffIcon,
  ShoppingCartOutlined as ShoppingCartIcon,
  PointOfSaleOutlined as PointOfSaleIcon,
  AccountBalanceOutlined as BankIcon,
  CreditCardOutlined as CreditCardIcon,
  GroupOutlined as GroupIcon,
  MenuOutlined as MenuIcon,
  HistoryOutlined as HistoryIcon,
  ChevronRight as ChevronRightIcon,
  AssessmentOutlined as AssessmentIcon,
} from "@mui/icons-material"
import { tokens } from "../../theme"
import "react-pro-sidebar/dist/css/styles.css"
import logo from "../../assets/6-removebg.png"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"

const Item = ({ title, to, icon, selected, setSelected, colors, theme }) => {
  const isDark = theme.palette.mode === "dark"
  const textColor = isDark ? colors.grey[100] : colors.grey[900]
  const hoverBgColor = isDark ? `rgba(34, 197, 94, 0.15)` : `rgba(34, 197, 94, 0.1)`
  const activeBgColor = isDark ? `rgba(34, 197, 94, 0.25)` : `rgba(34, 197, 94, 0.15)`

  return (
    <MenuItem
      active={selected === title}
      style={{
        backgroundColor: selected === title ? activeBgColor : "transparent",
        color: textColor,
        borderRadius: "8px",
        margin: "4px 0",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        paddingLeft: "8px",
        minHeight: "40px",
        borderLeft: selected === title ? `3px solid ${colors.primary[500]}` : "3px solid transparent",
      }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Typography
          variant="body2"
          fontWeight={selected === title ? 700 : 500}
          color={textColor}
          sx={{
            display: "block",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "13px",
          }}
        >
          {title}
        </Typography>
        {selected === title && (
          <ChevronRightIcon
            sx={{
              fontSize: "16px",
              color: colors.primary[500],
              transition: "all 0.3s ease",
            }}
          />
        )}
      </Box>
      <Link to={to} />
    </MenuItem>
  )
}

const Sidebar = ({ setIsCollapsed }) => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const isDark = theme.palette.mode === "dark"
  const [isCollapsed, setIsCollapsedState] = useState(false)
  const [selected, setSelected] = useState(localStorage.getItem("selectedMenu") || "Dashboard")
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"))
  const { isAuthenticated, user } = useSelector((state) => state.user)

  const bgColor = isDark ? "#0f172a" : "#ffffff"
  const textColor = isDark ? colors.grey[100] : colors.grey[900]
  const borderColor = isDark ? `rgba(34, 197, 94, 0.1)` : `rgba(34, 197, 94, 0.1)`

  useEffect(() => {
    localStorage.setItem("selectedMenu", selected)
  }, [selected])

  useEffect(() => {
    setIsCollapsed(isCollapsed)
  }, [isCollapsed])

  const handleCollapse = () => {
    setIsCollapsedState(!isCollapsed)
  }

  return (
    <Box
      sx={{
        position: "fixed",
        height: "100vh",
        overflowY: "auto",
        zIndex: 1000,
        width: isCollapsed ? "80px" : "275px",
        transition: "width 0.3s ease",
        color: textColor,
        boxShadow: isDark ? "4px 0 16px rgba(0, 0, 0, 0.4)" : "4px 0 16px rgba(34, 197, 94, 0.08)",
        background: bgColor,
        scrollBehavior: "smooth",
        borderRight: `1px solid ${borderColor}`,
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: isDark ? "#0f172a" : "#f5f5f5",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#22c55e",
          borderRadius: "3px",
          "&:hover": {
            background: "#16a34a",
          },
        },
      }}
    >
      <ProSidebar
        collapsed={isCollapsed}
        style={{
          background: "transparent",
        }}
      >
        <Menu iconShape="circle">
          <MenuItem
            onClick={handleCollapse}
            icon={isCollapsed ? <MenuIcon sx={{ color: colors.primary[500] }} /> : undefined}
            style={{
              margin: "10px 0 12px 0",
              color: colors.primary[500],
              backgroundColor: "transparent",
              transition: "all 0.3s ease",
              minHeight: "36px",
            }}
          >
            {!isCollapsed && (
              <Box display="flex" justifyContent="space-between" alignItems="center" ml="8px" width="100%">
                <Typography
                  variant="h6"
                  fontWeight={800}
                  letterSpacing={1}
                  sx={{
                    color: colors.primary[500],
                    fontSize: "16px",
                  }}
                >
                  SOBEP
                </Typography>
                <IconButton
                  onClick={handleCollapse}
                  sx={{
                    color: colors.primary[500],
                    padding: "4px",
                    "&:hover": {
                      color: colors.primary[600],
                      backgroundColor: colors.primary[700],
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <MenuIcon sx={{ fontSize: "20px" }} />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {!isCollapsed && isAuthenticated && (
            <Box
              sx={{
                textAlign: "center",
                mb: "16px",
                mx: "12px",
                p: "12px",
                background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 100%)`,
                borderRadius: "12px",
                border: "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 16px ${colors.primary[600]}40`,
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "inline-block",
                  mb: "8px",
                }}
              >
                <img
                  src={logo || "/placeholder.svg"}
                  alt="Logo"
                  width="56px"
                  height="56px"
                  style={{
                    borderRadius: "50%",
                    backgroundColor: "white",
                    border: "3px solid white",
                    transition: "all 0.3s ease",
                    boxShadow: `0 0 12px ${colors.primary[500]}40`,
                  }}
                />
              </Box>
              <Typography
                variant="subtitle2"
                color="white"
                fontWeight={700}
                sx={{
                  textTransform: "capitalize",
                  fontSize: "12px",
                }}
              >
                {user?.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#e0e0e0",
                  display: "block",
                  mt: "2px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontSize: "10px",
                }}
              >
                {user?.role}
              </Typography>
            </Box>
          )}

          <Box paddingLeft={isCollapsed ? undefined : "8px"} paddingRight={isCollapsed ? undefined : "8px"}>
            <Item
              title="Dashboard"
              to="/dashboard"
              icon={
                <HomeIcon
                  sx={{
                    color: selected === "Dashboard" ? colors.primary[500] : isDark ? "#666666" : "#999999",
                    fontSize: "20px",
                    transition: "all 0.3s ease",
                  }}
                />
              }
              selected={selected}
              setSelected={setSelected}
              colors={colors}
              theme={theme}
            />
            <Item
              title="Produits"
              to="/produit"
              icon={
                <InventoryIcon
                  sx={{
                    color: selected === "Produits" ? colors.primary[500] : isDark ? "#666666" : "#999999",
                    fontSize: "20px",
                    transition: "all 0.3s ease",
                  }}
                />
              }
              selected={selected}
              setSelected={setSelected}
              colors={colors}
              theme={theme}
            />
            <Item
              title="Les depenses"
              to="/depenses"
              icon={
                <MoneyOffIcon
                  sx={{
                    color: selected === "Les depenses" ? colors.primary[500] : isDark ? "#666666" : "#999999",
                    fontSize: "20px",
                    transition: "all 0.3s ease",
                  }}
                />
              }
              selected={selected}
              setSelected={setSelected}
              colors={colors}
              theme={theme}
            />
            <Item
              title="Ventes produits"
              to="/vente"
              icon={
                <ShoppingCartIcon
                  sx={{
                    color: selected === "Ventes produits" ? colors.primary[500] : isDark ? "#666666" : "#999999",
                    fontSize: "20px",
                    transition: "all 0.3s ease",
                  }}
                />
              }
              selected={selected}
              setSelected={setSelected}
              colors={colors}
              theme={theme}
            />

            <SubMenu
              title="Les ventes"
              icon={
                <PointOfSaleIcon
                  sx={{
                    color: selected?.includes("vente") ? colors.primary[500] : isDark ? "#666666" : "#999999",
                    fontSize: "20px",
                    transition: "all 0.3s ease",
                  }}
                />
              }
              style={{
                margin: "4px 0",
                borderRadius: "8px",
                color: textColor,
              }}
            >
              <Item
                title="Vente cash"
                to="/transaction/sales"
                icon={
                  <PointOfSaleIcon
                    sx={{
                      fontSize: "18px",
                      color: colors.primary[500],
                    }}
                  />
                }
                selected={selected}
                setSelected={setSelected}
                colors={colors}
                theme={theme}
              />
              <Item
                title="Vente Banque"
                to="/transaction/banque"
                icon={
                  <BankIcon
                    sx={{
                      fontSize: "18px",
                      color: colors.primary[500],
                    }}
                  />
                }
                selected={selected}
                setSelected={setSelected}
                colors={colors}
                theme={theme}
              />
              <Item
                title="Vente avec credit"
                to="/transaction/debt"
                icon={
                  <CreditCardIcon
                    sx={{
                      fontSize: "18px",
                      color: colors.primary[500],
                    }}
                  />
                }
                selected={selected}
                setSelected={setSelected}
                colors={colors}
                theme={theme}
              />
              <Item
                title="Générer Rapports"
                to="/generate-reports"
                icon={
                  <AssessmentIcon
                    sx={{
                      fontSize: "18px",
                      color: colors.primary[500],
                    }}
                  />
                }
                selected={selected}
                setSelected={setSelected}
                colors={colors}
                theme={theme}
              />
            </SubMenu>

            <Item
              title="Les interets et dettes"
              to="/product-analysis"
              icon={
                <HistoryIcon
                  sx={{
                    color: selected === "Les interets et dettes" ? colors.primary[500] : isDark ? "#666666" : "#999999",
                    fontSize: "20px",
                    transition: "all 0.3s ease",
                  }}
                />
              }
              selected={selected}
              setSelected={setSelected}
              colors={colors}
              theme={theme}
            />
            <Item
              title="Gestions utilisateurs"
              to="/team"
              icon={
                <GroupIcon
                  sx={{
                    color: selected === "Gestions utilisateurs" ? colors.primary[500] : isDark ? "#666666" : "#999999",
                    fontSize: "20px",
                    transition: "all 0.3s ease",
                  }}
                />
              }
              selected={selected}
              setSelected={setSelected}
              colors={colors}
              theme={theme}
            />
          </Box>
        </Menu>
      </ProSidebar>

      {isSmallScreen && (
        <Box position="fixed" bottom="20px" left="20px" zIndex={1001}>
          <IconButton
            onClick={handleCollapse}
            sx={{
              backgroundColor: colors.primary[500],
              color: "white",
              width: 48,
              height: 48,
              boxShadow: `0 4px 12px ${colors.primary[500]}40`,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: colors.primary[600],
                transform: "scale(1.1)",
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  )
}

export default Sidebar
